import { PICKUPS_GRID_FILTERS } from '../../Grid/Filter/DEFAULT_constants';
import { getDuplicatePickupsByPickupId } from '../../../../utils/utils';
import { getZoneValue } from '../../../utils/index';
import { isGroupHeaders, isGroupedPickups, isSingleInGroupPickups } from '../utils';

/**
 * GroupDataManager - Handles group management and data processing for GroupPickup component
 * 
 * This manager handles:
 * - Group expansion/collapse
 * - Grid data processing
 * - Grid context updates
 * - Group header refresh
 */
export class GroupDataManager {
  constructor(component) {
    this.component = component;
  }

  // Toggle group expansion
  toggleGroup = (groupId) => {
    // Set flag to prevent any selection changes during expand/collapse
    this.component.isExpandCollapseInProgress = true;

    this.component.setState(
      (prevState) => {
        const newExpanded = new Set(prevState.expandedGroups);

        if (newExpanded.has(groupId)) {
          newExpanded.delete(groupId);
        } else {
          newExpanded.add(groupId);
        }

        return { expandedGroups: newExpanded };
      },
      () => {
        // Update grid context with new expansion state
        this.updateGridContext();

        // Refresh grid cells to update the expand/collapse icons
        this.component.gridApi.refreshCells({ columns: ['isMergedPickup'], force: true });

        // Clear the flag after expand/collapse is complete
        setTimeout(() => {
          this.component.isExpandCollapseInProgress = false;
        }, 100);
      }
    );
  };

  onGroupClick = (event) => {
    // Delegate to selection manager's handleSelection
    this.component.selectionManager.handleSelection(event);
  };

  getVisibleRows = (allRows, expandedGroups = null) => {
    if (!allRows?.length) return [];

    const groupsToUse = expandedGroups || this.component.state.expandedGroups;
    const result = [];

    // Sort by creation date (newest first) before processing
    const sortedRows = [...allRows].sort((a, b) => {
      const aDate = new Date(a.pickupDate || a.createdAt || 0);
      const bDate = new Date(b.pickupDate || b.createdAt || 0);
      return bDate - aDate; // Descending order (newest first)
    });

    // Pre-build group children map for O(1) lookup - single pass O(n)
    const groupChildrenMap = new Map();
    sortedRows.forEach((row) => {
      if (isGroupedPickups(row) && row.groupId) {
        if (!groupChildrenMap.has(row.groupId)) {
          groupChildrenMap.set(row.groupId, []);
        }
        groupChildrenMap.get(row.groupId).push(row);
      }
    });

    // Build result in sorted order - O(n)
    sortedRows.forEach((row) => {
      if (isGroupHeaders(row)) {
        result.push(row);
        if (groupsToUse.has(row.id)) {
          const children = groupChildrenMap.get(row.id);
          if (children) {
            result.push(...children);
          }
        }
      } else if (isSingleInGroupPickups(row)) {
        result.push(row);
      }
    });

    return result;
  };

  // Simplified data processing - use existing grouped data from pickupsList
  getGridData = () => {
    const { pickupsFilter, pickupsList, duplicatePickups } = this.component.props.DispatchStore;

    // Get raw data (same as regular Pickups Grid)
    let rawPickups;
    if (pickupsFilter.value === PICKUPS_GRID_FILTERS.possibleMerge) {
      const groupedPickups = pickupsList.reduce((acc, pickup) => {
        getDuplicatePickupsByPickupId(duplicatePickups, pickup.pickupId).forEach((id) => {
          if (!acc.some((p) => p.pickupId.toLowerCase() === id.toLowerCase())) {
            const duplicatePickup = pickupsList.find(
              (p) => p.pickupId.toLowerCase() === id.toLowerCase()
            );
            if (duplicatePickup) {
              acc.push(duplicatePickup);
            }
          }
        });
        return acc;
      }, []);
      rawPickups = groupedPickups.length > 0 ? groupedPickups : [];
    } else {
      rawPickups = pickupsList.slice();
    }

    return rawPickups || [];
  };

  selectAllPickupsWithSameGroupId = () => {
    if (!this.component.gridApi) return;

    const { selectedPickupsList = [], pickupsList = [] } = this.component.props.DispatchStore;
    if (!selectedPickupsList.length) return;

    const groupIds = new Set(selectedPickupsList.map((pickup) => pickup?.groupId).filter(Boolean));
    if (!groupIds.size) return;

    const pickupIdsWithSameGroupId = pickupsList
      .filter(
        (pickup) =>
          groupIds.has(pickup.groupId) &&
          (isGroupedPickups(pickup) || isSingleInGroupPickups(pickup))
      )
      .map((pickup) => pickup.pickupId);

    this.component.props.glEventHub.emit('UpdateDispatchStore', 'updateSelectedPickups', [
      pickupIdsWithSameGroupId
    ]);

    this.component.gridApi.forEachNode((node) => {
      if (node?.data?.groupId && groupIds.has(node.data.groupId)) {
        node.setSelected(true, false);
      }
    });
  };

  // Refresh grid headers when pickup data is updated
  refreshGridHeaders = () => {
    if (this.component.gridApi) {
      // Collect all group header nodes first, then batch refresh (much faster than individual refreshes)
      const groupHeaderNodes = [];
      this.component.gridApi.forEachNode((node) => {
        if (node.data && isGroupHeaders(node.data)) {
          groupHeaderNodes.push(node);
        }
      });

      if (groupHeaderNodes.length > 0) {
        // Batch refresh all group headers in one call (much faster than individual refreshes)
        const columnsToRefresh = [
          'shipperName',
          'pickupNumberList', // Group pickup numbers
          'bills', // Aggregated bills count
          'pieces', // Aggregated pieces count
          'hu', // Aggregated handling units
          'weight', // Aggregated weight
          'driverName', // Driver assignment info
          'routeName' // Route assignment info
        ];

        this.component.gridApi.refreshCells({
          rowNodes: groupHeaderNodes,
          columns: columnsToRefresh,
          force: true
        });
      }
    }
  };

  // Update grid context with fresh group counts
  updateGridContext = (skipRefresh = false) => {
    this.component.gridContext = {
      ...this.component.gridContext,
      expanded: this.component.state.expandedGroups,
      toggle: this.toggleGroup,
      onGroupClick: this.component.selectionManager.handleSelection, // Use unified handler
      isGroupSelected: this.component.selectionManager.isGroupSelected,
      getZoneValue: getZoneValue
    };

    // Force refresh of pickup number cells when context changes
    if (!skipRefresh && this.component.gridApi && typeof this.component.gridApi.refreshCells === 'function') {
      setTimeout(() => {
        this.component.gridApi.refreshCells({ columns: ['pickupNumberList'], force: true });
      }, 0);
    }
  };

  // Initialize expanded groups on component mount - start with all groups collapsed
  initializeExpandedGroups = () => {
    const { pickupsList } = this.component.props.DispatchStore;
    if (pickupsList && pickupsList.length > 0) {
      // Start with all groups collapsed by default
      const defaultExpanded = new Set();

      this.component.setState({
        expandedGroups: defaultExpanded
      });
    } else {
      this.component.setState({
        expandedGroups: new Set()
      });
    }
  };

  // Re-initialize expanded groups when data changes - preserve collapsed state
  reinitializeExpandedGroups = () => {
    const { pickupsList } = this.component.props.DispatchStore;
    if (pickupsList && pickupsList.length > 0) {
      // Maintain collapsed state by default - don't auto-expand groups
      const defaultExpanded = new Set();

      // Sort arrays for comparison (don't mutate originals)
      const currentExpanded = [...this.component.state.expandedGroups].sort((a, b) => a.localeCompare(b));
      const newExpanded = Array.from(defaultExpanded).sort((a, b) => a.localeCompare(b));

      // Only update if there's actually a difference (preserve user's manual expansions)
      if (JSON.stringify(currentExpanded) !== JSON.stringify(newExpanded)) {
        this.component.setState({
          expandedGroups: defaultExpanded
        });
      }
    }
  };
}

